import {generate} from "../../../routing/routes";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {useDispatch, useSelector} from "react-redux";
import { cancelAction, deleteDraft } from "../../../data/followup/actions/followup";
import {serviceName} from "../../../data/booking/constants/service_types";
import { useTranslation } from "react-i18next";
import moment from 'moment';
import cx from 'classnames';
import isEmpty from "lodash/isEmpty";
import Tooltip from "../../shared/components/Tooltip";
import DropdownMenu from "../../shared/components/DropdownMenu";
import DeleteModal from "../../shared/components/DeleteModal";
import { AlternateDeleteButton } from "../../shared/components/Buttons";
import { ChevronRightIcon, TrashIcon, EyeIcon, XMarkIcon } from "@heroicons/react/20/solid";
import { Desktop, Mobile } from "../../shared/components/Responsive";


const getEndUserName = (endUser) => {
    if (!isEmpty(endUser.firstName) && !isEmpty(endUser.lastName)) {
        return endUser.firstName + ' ' + endUser.lastName;
    }

    if (!isEmpty(endUser.firstName) && isEmpty(endUser.lastName)) {
        return endUser.firstName;
    }

    return endUser.lastName;
};

export const TripItem = ({ trip, editable = false, isLoading = false, endUser = false, translationFor, statusName }) => {
    let isCancelled = trip.status =='cancelled' || trip.status =='rejected';
    let isDraft = trip.status =='draft';
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const formattedDate = new Date(trip.createdAt);
    const [modalOpen, setModalOpen] = useState(false);
    const [dropdownIsOpen, setDropdownIsOpen] = useState(false);
    const isDeletingQuotation = useSelector(state => state.account.quotation.isDeleting)
    const isDeletingReservation = useSelector(state => state.account.booking.isDeleting)

    const isDeleting = () => {
        return isDeletingQuotation == trip.reference || isDeletingReservation == trip.reference;
    }

    const handleNavigate = () => {
        const route = !isDraft ? generate('customer_reservation', { reference: trip.reference }) : generate('book_edit', { reference: trip.reference });
        navigate(route);
    };

    const handleOpenModal = (event) => {
        event.stopPropagation();
        setModalOpen(true);
    };

    const handleDeleteReservation = (reference, type) => {
        dispatch(cancelAction(reference, type));
    };

    const mobileContent =
    <div className="flex flex-col items-center justify-center text-center w-full">
            <div>
                {trip.routes.map((route)=>(
                    <p key={route.id}>{route.title}</p>
                ))}
            </div>
            <hr className="my-1 border-1 border-solid border-stone-300 w-1/2"/>
            <div>
                {trip.services.map((service, index) => (
                <span key={index}>
                    {index != 0 && " · "}
                    {serviceName(service.type)}
                </span>
                ))}
            </div>
            <div className="flex gap-4">
                <button 
                    onClick={handleNavigate}
                    className="group flex justify-around items-center gap-2 mt-3 p-2 bg-orange-600 hover:bg-orange-700 text-white cursor-pointer rounded-md"
                >
                    <p className='group-hover:underline'>{t('account_button.details')}</p>
                    <EyeIcon className="h-6 w-6" />
                </button>
                {(!isCancelled && (trip.startAt> (moment().add(3, "day").format()) || isDraft) )&& 
                    <AlternateDeleteButton
                        type="button"
                        disabled={isDeleting()}
                        loading={isDeleting()}
                        className={cx("flex justify-around items-center gap-2 my-3 p-2 bg-red-600 text-white rounded-md hover:underline", {
                            "bg-stone-400 cursor-not-allowed" : isDeleting(),
                            "hover:bg-red-700 cursor-pointer" : !isDeleting()
                        })}
                        onClick={handleOpenModal}
                    >
                        {t('booking.cancel')} <TrashIcon className="h-5 w-5" />
                    </AlternateDeleteButton>
                }
            </div>
    </div>;

    return (<>
        <Desktop>
            <tr onClick={handleNavigate}
            className={cx("group relative text-center hover:bg-[#F97316] hover:cursor-pointer", {
                "opacity-50": isLoading || isCancelled
            })}>
                <td className="bg-stone-200 text-gray-600 group-hover:text-white group-hover:bg-[#F97316]">
                    <div className="text-base flex justify-center items-center px-2 py-1">
                        <div className="flex flex-col flex-1">
                            <span className="text-xl">{moment(trip.startAt).format("DD")}</span>
                            <span>{moment(trip.startAt).format("MMM")}</span>
                            <span>{moment(trip.startAt).format("YYYY")}</span>
                        </div>
                        {trip.endAt && trip.startAt !== trip.endAt && (
                            <>
                                <ChevronRightIcon className="h-8 w-8" aria-hidden="true"/>
                                <div className="flex flex-col flex-1">
                                    <span className="text-xl">{moment(trip.endAt).format("DD")}</span>
                                    <span>{moment(trip.endAt).format("MMM")}</span>
                                    <span>{moment(trip.endAt).format("YYYY")}</span>
                                </div>
                            </>
                        )}
                    </div>
                    <span className="absolute bottom-0 left-0 right-0 h-px bg-stone-300" />
                </td>
                <td className={cx("p-2 text-gray-600 group-hover:text-white text-base")}>
                    <div className="">
                        {statusName(trip.status)}
                    </div>
                </td>
                <td className={cx("px-2 text-gray-600 group-hover:text-white text-base")}>
                    <div className="flex flex-col justify-center items-center">
                        {trip.reference}
                        <hr className="my-1 border-1 border-solid border-stone-300 w-1/2"/>
                        {(endUser && trip.endUser) && getEndUserName(trip.endUser)}
                    </div>
                </td>
                <td className={cx("text-gray-600 text-xs")}>
                    <div className="flex flex-col justify-center items-center group-hover:text-white">
                        {trip.routes.map((route)=>(
                        <p key={route.id}>{route.title}</p>
                        ))}

                        <hr className="my-1 border-1 border-solid border-stone-300 w-1/2"/>
                        <div>
                            {trip.services.map((service, index) => (
                            <span key={index}>
                                {index != 0 && " · "}
                                {serviceName(service.type)}
                            </span>
                            ))}
                        </div>
                    </div>
                </td>
                <td className={cx("px-2 text-gray-600 group-hover:text-white text-base")}>
                    {(!isCancelled && (trip.startAt> (moment().add(3, "day").format()) || isDraft) )&& <>
                        <Tooltip Icon={() => null} 
                            children={
                                <AlternateDeleteButton
                                    type="button"
                                    disabled={isDeleting()}
                                    loading={isDeleting()}
                                    className={cx("flex items-center justify-center w-8 h-8 text-white bg-orange-500 group-hover:text-orange-500 rounded-full", {
                                        "bg-stone-400 cursor-not-allowed" : isDeleting(),
                                        "group-hover:bg-white hover:scale-110" : !isDeleting()
                                    })}
                                    onClick={handleOpenModal}
                                >
                                    <TrashIcon className="h-5 w-5" />
                                </AlternateDeleteButton>
                            }
                            content={`${t('booking.cancel')} ${translationFor}`} 
                            className="flex w-8 h-8"
                        />                        
                        <DeleteModal
                            title={`${t('account_button.comfirmCancel')} ${translationFor}?`}
                            open={modalOpen}
                            setOpen={setModalOpen}
                            onConfirm={() => {isDraft ? dispatch(deleteDraft(trip)) : handleDeleteReservation(trip.reference, trip.type)}}
                        />
                    </>}
                </td>
            </tr>
        </Desktop>

        <Mobile>
            <div key={trip.id} className="relative bg-stone-100 text-gray-600 border border-stone-300 rounded-md">
                <div className="flex flex-col justify-between items-center gap-3 text-sm text-center">
                    <div className="w-full flex-wrap flex justify-center items-center gap-5 flex-1 p-2 bg-white border-b rounded-tr-md rounded-tl-lg">
                        <div className="flex gap-2">
                            <div className="flex gap-1 items-center">
                                <span>{moment(trip.startAt).format("DD MMM YYYY")}</span>
                            </div>
                            {trip.endAt && trip.startAt !== trip.endAt && <>
                                <div className="flex gap-2 items-center">
                                    <p className="text-xs text-gray-500">{">"}</p>
                                    <span>{moment(trip.endAt).format("DD MMM YYYY")}</span>
                                </div>
                            </>}
                        </div>
                        <div className="flex gap-2 items-center bg-gray-200 rounded-md px-2">
                            <span className="inline-block w-2 h-2 bg-gray-300 rounded-full"></span>
                            {statusName(trip.status)}
                        </div>
                    </div>
                    <div className="flex flex-col justify-center items-center gap-2 flex-1 ">
                        <p>{trip.reference}</p>
                        <p>{(endUser && trip.endUser) && getEndUserName(trip.endUser)}</p>
                    </div>
                </div>
                <button 
                    onClick={() => setDropdownIsOpen(!dropdownIsOpen)} 
                    className={cx("absolute left-1/2 transform -translate-x-1/2 px-5 py-1 rounded-md text-sm shadow-inner", {
                        "bg-gray-300 hover:bg-gray-700 hover:text-white bottom-[-16px]": dropdownIsOpen,
                        "bg-orange-600 hover:bg-orange-700 text-white bottom-[-14px]": !dropdownIsOpen
                    })} 
                >
                    {!dropdownIsOpen ? t("account_button.show_more") : t("account_button.show_less")}
                </button>
                <div className="mb-5">
                    <DropdownMenu isOpen={dropdownIsOpen} content={mobileContent} classname="flex py-3 mt-2 border-t" />
                </div>
                <div>
                    <DeleteModal
                        title={`${t('account_button.comfirmCancel')} ${translationFor}?`}
                        open={modalOpen}
                        setOpen={setModalOpen}
                        onConfirm={() => {handleDeleteReservation(trip.id)}}
                    />
                </div>
            </div>
        </Mobile>
    </>);
}